import { useState, useMemo } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription,
  SheetHeader, SheetTitle
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  Plus, Search, Eye, Edit, Power, MoreHorizontal,
  UserPlus, Users, ChevronDown, ChevronUp, Download,
  MapPin, Shield, Info
} from "lucide-react";
import { useEmployees } from "@/hooks/useEmployees";
import { RemovedEmployeesCard } from "@/components/corporate/organizations/RemovedEmployeesCard";
import type { EmployeeResponse } from "@/types/employee";
import { PhoneInput, formatPhoneForAPI, type PhoneValue } from "@/components/Form/PhoneInput";

interface EmployeesTabProps {
  organizationId: string;
}

export default function EmployeesTab({ organizationId }: EmployeesTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [editingEmployee, setEditingEmployee] = useState<EmployeeResponse | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeResponse | null>(null);
  const [sortField, setSortField] = useState<"name" | "department" | "status">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [statusFilter, setStatusFilter] = useState<string>("all");




  // Fetch data using new hooks
  const { employees, isLoading } = useEmployees(organizationId);




  // Filter and sort employees
  const filteredAndSortedEmployees = useMemo(() => {
    return employees
      .filter(employee => {
        // Text search
        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.toLowerCase();
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          fullName.includes(query) ||
          (employee.email?.toLowerCase() || '').includes(query) ||
          (employee.departmentName?.toLowerCase() || '').includes(query) ||
          (employee.jobTitle?.toLowerCase() || '').includes(query)
        );

        // Status filter (based on isActive boolean)
        const matchesStatus = statusFilter === "all" ||
          (statusFilter === "active" && employee.isActive) ||
          (statusFilter === "inactive" && !employee.isActive);

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        let aValue: any, bValue: any;

        if (sortField === "name") {
          aValue = `${a.firstName || ''} ${a.lastName || ''}`;
          bValue = `${b.firstName || ''} ${b.lastName || ''}`;
        } else if (sortField === "department") {
          aValue = a.departmentName || '';
          bValue = b.departmentName || '';
        } else if (sortField === "status") {
          aValue = a.isActive ? 'active' : 'inactive';
          bValue = b.isActive ? 'active' : 'inactive';
        } else {
          aValue = a[sortField as keyof EmployeeResponse];
          bValue = b[sortField as keyof EmployeeResponse];
        }

        const modifier = sortOrder === "asc" ? 1 : -1;

        if (aValue < bValue) return -1 * modifier;
        if (aValue > bValue) return 1 * modifier;
        return 0;
      });
  }, [employees, searchQuery, sortField, sortOrder, statusFilter]);


  const handleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getInitials = (employee: EmployeeResponse) => {
    // Use firstName/lastName from Employee type
    const firstName = employee.firstName || '';
    const lastName = employee.lastName || '';
    if (!firstName && !lastName) return "??";
    const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
    return initials || "??";
  };

  const getRoleName = (role: string): "Admin" | "Manager" | "Employee" | "Invalid" => {
    switch (role) {
      case "OrganizationAdmin":
        return "Admin";
      case "OrganizationManager":
        return "Manager";
      case "OrganizationEmployee":
        return "Employee";
      default:
        return "Invalid";
    }
  };

  // Helper function to get primary role for display
  const getPrimaryRole = (roles: string[]): string => {
    if (!roles || roles.length === 0) return "employee";
    return roles[0];
  };

  // Role permissions mapping for display
  const ROLE_PERMISSIONS = {
    admin: { label: "Admin" },
    editor: { label: "Editor" },
    employee: { label: "Employee" }
  };

  const getStatusBadgeVariant = (isActive: boolean): "default" | "destructive" => {
    return isActive ? "default" : "destructive";
  };

  const getStatusText = (isActive: boolean): string => {
    return isActive ? "Active" : "Inactive";
  };


  // Employee Detail Drawer
  const renderEmployeeDetailDrawer = () => {
    if (!selectedEmployee) return null;

    return (
      <Sheet open={!!selectedEmployee} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={selectedEmployee.photo?.url} />
                <AvatarFallback>{getInitials(selectedEmployee)}</AvatarFallback>
              </Avatar>
              <div>
                <SheetTitle>{`${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim()}</SheetTitle>
                <Badge variant={getStatusBadgeVariant(selectedEmployee.isActive)}>
                  {getStatusText(selectedEmployee.isActive)}
                </Badge>
              </div>
            </div>
          </SheetHeader>

          {/* Details only (the fake Activities / Performance / History tabs and the dead Reset Password are gone). */}
          <div className="mt-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{`${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedEmployee.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Department</Label>
                  <p className="font-medium">{selectedEmployee.departmentName || "Not Assigned"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Position</Label>
                  <p className="font-medium">{selectedEmployee.jobTitle || selectedEmployee.jobTitle || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Roles</Label>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {selectedEmployee.roles.map((role, index) => (
                      <Badge key={index} variant="outline">{getRoleName(role)}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge variant={getStatusBadgeVariant(selectedEmployee.isActive)} className="mt-1">
                    {getStatusText(selectedEmployee.isActive)}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Location</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium">
                      {selectedEmployee.locationName || "Not Assigned"}
                    </p>
                  </div>
                </div>
                {selectedEmployee.createdAt && (
                  <div>
                    <Label className="text-muted-foreground">Join Date</Label>
                    <p className="font-medium">
                      {new Date(selectedEmployee.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
          </div>

        </SheetContent>
      </Sheet>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Employees</CardTitle>
          <CardDescription>Loading employee data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar, Filters and Add Button */}
      <div className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, department, or position..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {statusFilter !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>


      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          {filteredAndSortedEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">No employees yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                No employees found
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-muted/70"
                      onClick={() => handleSort("name")}
                    >
                      <span className="flex items-center gap-1">
                        Full Name
                        {sortField === "name" && (
                          sortOrder === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </span>
                    </th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-muted/70"
                      onClick={() => handleSort("department")}
                    >
                      <span className="flex items-center gap-1">
                        Department
                        {sortField === "department" && (
                          sortOrder === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </span>
                    </th>
                    <th className="p-3 text-left">Position</th>
                    <th className="p-3 text-left">Roles</th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-muted/70"
                      onClick={() => handleSort("status")}
                    >
                      <span className="flex items-center gap-1">
                        Status
                        {sortField === "status" && (
                          sortOrder === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </span>
                    </th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedEmployees.map((employee) => (
                    <tr
                      key={employee.id}
                      className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={employee.photo?.url} />
                            <AvatarFallback>{getInitials(employee)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="font-medium">{`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}</span>
                            <div className="text-xs text-muted-foreground">{employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-sm">{employee.departmentName || "Not Assigned"}</td>
                      <td className="p-3 text-sm">{employee.jobTitle || "N/A"}</td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          {employee.roles.map((role, index) => (
                            <Badge key={index} variant="outline">{getRoleName(role)}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant={getStatusBadgeVariant(employee.isActive)}>
                          {getStatusText(employee.isActive)}
                        </Badge>
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Employee actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedEmployee(employee)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {filteredAndSortedEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className="p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedEmployee(employee)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={employee.photo?.url} />
                        <AvatarFallback>{getInitials(employee)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-medium">{`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}</h4>
                        <p className="text-sm text-muted-foreground">
                          {employee.departmentName || "Not Assigned"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {employee.jobTitle || "N/A"}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Employee actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedEmployee(employee)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      Employee
                    </Badge>
                    <Badge variant={getStatusBadgeVariant(employee.isActive)}>
                      {getStatusText(employee.isActive)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Removed employees — read-only; hidden when nobody is removed */}
      <RemovedEmployeesCard organizationId={organizationId} />

      {/* Modals and Drawers */}
      {renderEmployeeDetailDrawer()}
    </div>
  );
}